/**
 * Command palette — every site, ticket, page and action one keystroke away.
 *
 * Opened with ⌘K / Ctrl+K, or by pressing Enter in the header search when the
 * query matches no navigation item (AppShell's `onGlobalSearch`), so the portal
 * gains a palette without growing a second search box.
 *
 * Accessibility: this is the combobox-with-listbox pattern, not a grid of
 * buttons. Focus stays in the input the whole time and the active option is
 * pointed at with aria-activedescendant, which means there is no focus to trap
 * — Tab or Escape simply closes it and focus returns where it came from.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { filterCommands, type CommandItem, type CommandKind } from '../lib/commandRanking';

export type { CommandItem, CommandKind } from '../lib/commandRanking';

export function CommandPalette({
  open,
  items,
  initialQuery,
  onClose,
}: {
  open: boolean;
  items: CommandItem[];
  initialQuery?: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const baseId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const openerRef = useRef<Element | null>(null);
  const [query, setQuery] = useState(initialQuery ?? '');
  const [activeIndex, setActiveIndex] = useState(0);

  const results = useMemo(() => filterCommands(items, query), [items, query]);

  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement;
    setQuery(initialQuery ?? '');
    setActiveIndex(0);
    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(focusTimer);
      // Focus returns to whatever opened the palette.
      if (openerRef.current instanceof HTMLElement) openerRef.current.focus();
    };
  }, [open, initialQuery]);

  // Keep the active option in view as the arrows move through a long list.
  useEffect(() => {
    if (!open) return;
    const node = listRef.current?.children[activeIndex];
    if (node instanceof HTMLElement) node.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  if (!open) return null;

  const run = (item: CommandItem | undefined) => {
    if (!item) return;
    onClose();
    item.run();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' || event.key === 'Tab') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => (results.length === 0 ? 0 : (index + 1) % results.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => (results.length === 0 ? 0 : (index - 1 + results.length) % results.length));
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(Math.max(0, results.length - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      run(results[activeIndex]);
    }
  };

  const kindLabel: Record<CommandKind, string> = {
    page: t('palette.kind_page'),
    site: t('palette.kind_site'),
    ticket: t('palette.kind_ticket'),
    action: t('palette.kind_action'),
  };

  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[10vh]">
      <div aria-hidden="true" className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('palette.title')}
        className="relative flex max-h-[70vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-xl"
        onKeyDown={onKeyDown}
      >
        <div className="border-b border-divider p-2">
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={results.length > 0 ? optionId(activeIndex) : undefined}
            aria-autocomplete="list"
            aria-label={t('palette.title')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(0);
            }}
            placeholder={t('palette.placeholder')}
            className="h-11 w-full rounded-sm bg-surface px-3 text-sm text-text outline-none placeholder:text-text-faint"
          />
        </div>

        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={t('palette.results')}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1"
        >
          {results.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-text-muted">{t('palette.empty')}</li>
          )}
          {results.map((item, index) => (
            <li
              key={item.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              onClick={() => run(item)}
              onMouseEnter={() => setActiveIndex(index)}
              className={`flex cursor-pointer items-center justify-between gap-3 rounded-md px-3 py-2 ${
                index === activeIndex ? 'bg-surface-3' : ''
              }`}
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-text">{item.label}</span>
                {item.hint && <span className="block truncate text-xs text-text-muted">{item.hint}</span>}
              </span>
              <span className="shrink-0 rounded-full bg-surface-2 px-2 py-0.5 text-[11px] text-text-muted">
                {kindLabel[item.kind]}
              </span>
            </li>
          ))}
        </ul>

        <p className="border-t border-divider px-3 py-2 text-[11px] text-text-faint">
          {t('palette.footer')}
        </p>
      </div>
    </div>
  );
}
